import React, {useEffect, useRef, useState} from 'react'
import {
  Autocomplete,
  Box,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import FilterListIcon from '@mui/icons-material/FilterList'
import SearchIcon from '@mui/icons-material/Search'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, {Dayjs} from 'dayjs'
import Button from 'src/components-new/primitives/Button'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import type {SxProps, Theme} from '@mui/material'
import PatientLookupDialog from './PatientLookupDialog'
import DoctorsLookupDialog from './DoctorsLookupDialog'

export type FilterFieldType =
  | 'select'
  | 'multiSelect'
  | 'datePicker'
  | 'rangePicker'
  | 'timePicker'
  | 'range'
  | 'checkbox'
  | 'text'
  | 'string'
  | 'patientLookup'
  | 'doctorLookup'

export interface FilterItem {
  id: string
  label: string
  type: FilterFieldType
  options?: {value: string; label: string}[]
  // Alternative to static `options`, for a field whose choices come from an API call
  // instead of a fixed list (e.g. province/città cascading off region/provincia). Called
  // on mount for a field with no `dependsOn`, and again whenever the field it `dependsOn`
  // changes — receives the current filter values so it can scope the request accordingly.
  loadOptions?: (values: Record<string, any>) => Promise<{value: string; label: string}[]>
  // Id of another FilterItem in the same config this field's options depend on. While
  // that field is empty, this one renders disabled instead of calling `loadOptions`; once
  // it changes, this field's own value is cleared (and its `loadOptions` re-run) so it
  // never shows stale choices from the previous parent selection.
  dependsOn?: string
  // Swaps the 'select' field's plain dropdown for an Autocomplete so the user can type to
  // filter the list (e.g. "M" narrows Città down to comuni containing it) instead of
  // scrolling — same showSearch convention as SelectNew, for the same long-list cases
  // (region/provincia/città and the like).
  showSearch?: boolean
  defaultValue?: any
  min?: number
  max?: number
  step?: number
  valueLabelFormat?: (value?: number) => string
}

export interface FilterResult {
  property: string
  type: string
  value: any
}

interface FiltersProps {
  config: FilterItem[]
  onSubmit: (values: FilterResult[]) => void
  triggerLabel?: string
  doctorCustomFilters?: Record<string, string | string[]>[]
}

type LookupType = 'patientLookup' | 'doctorLookup'

// Il Drawer dei Filtri può essere aperto sopra un Dialog (es. ActivityDetailDialogNew),
// quindi va sopra theme.zIndex.modal (1300). Select/DatePicker/PatientLookupDialog/
// DoctorsLookupDialog però aprono a loro volta un Popover/Popper/Dialog che di
// default usa lo stesso theme.zIndex.modal: senza spingerli oltre il Drawer stesso,
// i loro popup si aprirebbero dietro il Drawer che li contiene (visibili solo il
// trigger, non il contenuto del popup/dialog).
const FILTERS_DRAWER_Z_INDEX = (theme: Theme) => theme.zIndex.modal + 1
const FILTERS_POPUP_Z_INDEX = (theme: Theme) => theme.zIndex.modal + 2

function buildDefaultValues(config: FilterItem[]): Record<string, any> {
  const defaults: Record<string, any> = {}
  config.forEach(({id, type, defaultValue, min, max}) => {
    if (type === 'range') {
      defaults[id] = defaultValue ?? [min ?? 0, max ?? 100]
    } else if (type === 'checkbox' || type === 'multiSelect') {
      defaults[id] = defaultValue ?? []
    } else {
      defaults[id] = defaultValue ?? ''
    }
  })
  return defaults
}

function buildPostBody(
  values: Record<string, any>,
  rangeDates: Record<string, [Dayjs | null, Dayjs | null]>,
  lookupData: Record<string, any>,
  config: FilterItem[],
): FilterResult[] {
  return config
    .map((item) => {
      if (item.type === 'patientLookup' || item.type === 'doctorLookup') {
        const val = lookupData[item.id]
        if (!val) return null
        return {property: item.id, type: item.type, value: val}
      }
      if (item.type === 'rangePicker') {
        const [from, to] = rangeDates[item.id] ?? [null, null]
        if (!from && !to) return null
        return {
          property: item.id,
          type: item.type,
          value: [
            from ? from.startOf('day').toISOString() : null,
            to ? to.endOf('day').toISOString() : null,
          ],
        }
      }
      const raw = values[item.id]
      if (item.type === 'datePicker') {
        if (!raw) return null
        return {property: item.id, type: item.type, value: (raw as Dayjs).startOf('day').toISOString()}
      }
      if (item.type === 'timePicker') {
        if (!raw) return null
        return {property: item.id, type: item.type, value: (raw as Dayjs).toISOString()}
      }
      if (!raw && raw !== 0) return null
      if (Array.isArray(raw) && raw.length === 0) return null
      // Fall back to 'text' when the PSP config omits `type` on a filter item: the
      // backend's schema requires it, and `undefined` gets silently dropped by JSON
      // serialization, causing "body.filters.N must have required property 'type'".
      // 'text' also matches what the user actually saw, since renderField's default
      // branch (a plain TextField) is what's rendered whenever `item.type` is unset.
      return {property: item.id, type: item.type ?? 'text', value: raw}
    })
    .filter(Boolean) as FilterResult[]
}

export default function Filters({
  config,
  onSubmit,
  triggerLabel = 'Filtra',
  doctorCustomFilters = [],
}: FiltersProps): JSX.Element {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [values, setValues] = useState<Record<string, any>>(() => buildDefaultValues(config))
  const [rangeDates, setRangeDates] = useState<Record<string, [Dayjs | null, Dayjs | null]>>({})
  const [lookupData, setLookupData] = useState<Record<string, any>>({})
  const [lookupOpen, setLookupOpen] = useState<{id: string; type: LookupType} | null>(null)
  // Resolved options for `loadOptions` fields (region/provincia/città-style cascades) and
  // their in-flight state — kept apart from `options`, which stays whatever the config
  // declared, so a field can fall back to it if `loadOptions` was never configured.
  const [dynamicOptions, setDynamicOptions] = useState<Record<string, {value: string; label: string}[]>>({})
  const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({})

  const loadOptionsFor = async (item: FilterItem, valuesForLoad: Record<string, any>) => {
    if (!item.loadOptions) return
    setLoadingFields(prev => ({...prev, [item.id]: true}))
    try {
      const opts = await item.loadOptions(valuesForLoad)
      setDynamicOptions(prev => ({...prev, [item.id]: opts ?? []}))
    } catch {
      setDynamicOptions(prev => ({...prev, [item.id]: []}))
    } finally {
      setLoadingFields(prev => ({...prev, [item.id]: false}))
    }
  }

  // All fields (transitively) depending on `id`, closest first — used to clear a whole
  // cascade (e.g. changing regione clears both provincia and città, not just provincia).
  const collectDependentIds = (id: string): string[] => {
    const direct = config.filter(item => item.dependsOn === id).map(item => item.id)
    return direct.reduce<string[]>((acc, depId) => [...acc, ...collectDependentIds(depId)], [...direct])
  }

  const getOptions = (item: FilterItem): {value: string; label: string}[] =>
    item.loadOptions ? (dynamicOptions[item.id] ?? []) : (item.options ?? [])

  const initialized = useRef(false)
  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    const defaults = buildDefaultValues(config)
    setValues(defaults)
    const ranges: Record<string, [Dayjs | null, Dayjs | null]> = {}
    config.forEach(({id, type, defaultValue}) => {
      if (type === 'rangePicker') ranges[id] = defaultValue ?? [null, null]
    })
    setRangeDates(ranges)
    // Root fields (no dependsOn) with loadOptions fetch immediately; dependent fields wait
    // for their parent to be picked (see setField below).
    config
      .filter(item => item.loadOptions && !item.dependsOn)
      .forEach(item => loadOptionsFor(item, defaults))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setField = (id: string, value: any) => {
    const dependentIds = collectDependentIds(id)
    setValues(prev => {
      const next = {...prev, [id]: value}
      dependentIds.forEach(depId => {
        const depItem = config.find(item => item.id === depId)
        next[depId] = depItem?.type === 'multiSelect' || depItem?.type === 'checkbox' ? [] : ''
      })
      return next
    })
    if (dependentIds.length) {
      setDynamicOptions(prev => {
        const next = {...prev}
        dependentIds.forEach(depId => { next[depId] = [] })
        return next
      })
    }
    // Only the direct dependent gets re-fetched here — deeper ones stay cleared/disabled
    // until the user picks their own (now-reset) parent value in turn.
    if (value) {
      config
        .filter(item => item.dependsOn === id && item.loadOptions)
        .forEach(item => loadOptionsFor(item, {...values, [id]: value}))
    }
  }

  const handleClose = () => setAnchorEl(null)

  const handleApply = () => {
    onSubmit(buildPostBody(values, rangeDates, lookupData, config))
    handleClose()
  }

  const handleReset = () => {
    setValues(buildDefaultValues(config))
    const ranges: Record<string, [Dayjs | null, Dayjs | null]> = {}
    config.forEach(({id, type, defaultValue}) => {
      if (type === 'rangePicker') ranges[id] = defaultValue ?? [null, null]
    })
    setRangeDates(ranges)
    setLookupData({})
    // Root loadOptions fields (e.g. regione) keep their already-fetched list — it doesn't
    // depend on any filter value — only dependent fields (provincia/città) need clearing.
    setDynamicOptions(prev => {
      const next = {...prev}
      config.forEach(item => { if (item.dependsOn) next[item.id] = [] })
      return next
    })
    onSubmit([])
    handleClose()
  }

  const clearLookup = (id: string) => {
    setField(id, '')
    setLookupData(prev => {
      const next = {...prev}
      delete next[id]
      return next
    })
  }

  const renderField = (item: FilterItem) => {
    switch (item.type) {
      case 'select': {
        const options = getOptions(item)
        const parentItem = item.dependsOn ? config.find(c => c.id === item.dependsOn) : undefined
        const waitingForParent = Boolean(parentItem) && !values[parentItem!.id]
        const isLoading = Boolean(loadingFields[item.id])

        // Plain MUI Select only jumps to the option starting with the typed letter — it
        // doesn't narrow the list as you keep typing. Autocomplete does, so long lists
        // (regione/provincia/città and the like) opt into it via showSearch, same
        // convention as SelectNew.
        if (item.showSearch) {
          return (
            <Autocomplete
              size="small"
              options={options}
              value={options.find(opt => opt.value === values[item.id]) ?? null}
              onChange={(_e, opt) => setField(item.id, opt?.value ?? '')}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(opt, val) => opt.value === val.value}
              disabled={waitingForParent}
              loading={isLoading}
              loadingText="Caricamento..."
              noOptionsText="Nessuna opzione"
              componentsProps={{popper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}}}}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={item.label}
                  placeholder={waitingForParent ? `Seleziona prima ${parentItem!.label.toLowerCase()}` : 'Cerca...'}
                />
              )}
            />
          )
        }

        // Some PSP configs already ship their own "all" option (empty value, e.g.
        // "Tutti gli stati") inside item.options — adding the generic placeholder on
        // top of that produced two indistinguishable "select everything" entries in
        // the same dropdown. Only fall back to the generic placeholder when the
        // config doesn't already provide one.
        const hasOwnAllOption = options.some(opt => opt.value === '')
        return (
          <FormControl fullWidth size="small" disabled={waitingForParent}>
            <InputLabel>{item.label}</InputLabel>
            <Select
              label={item.label}
              value={waitingForParent ? '' : (values[item.id] ?? '')}
              onChange={(e: SelectChangeEvent) => setField(item.id, e.target.value)}
              MenuProps={{sx: {zIndex: FILTERS_POPUP_Z_INDEX}}}
            >
              {waitingForParent && (
                <MenuItem value="" disabled><em>{`Seleziona prima ${parentItem!.label.toLowerCase()}`}</em></MenuItem>
              )}
              {!waitingForParent && !hasOwnAllOption && (
                <MenuItem value=""><em>{isLoading ? 'Caricamento...' : 'Tutti'}</em></MenuItem>
              )}
              {!waitingForParent && options.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }

      case 'multiSelect': {
        const options = getOptions(item)
        return (
          <FormControl fullWidth size="small">
            <InputLabel>{item.label}</InputLabel>
            <Select
              label={item.label}
              multiple
              value={values[item.id] ?? []}
              onChange={(e: SelectChangeEvent<string[]>) => setField(item.id, e.target.value)}
              renderValue={(selected) => (selected as string[])
                .map(value => options.find(opt => opt.value === value)?.label ?? value)
                .join(', ')}
              MenuProps={{sx: {zIndex: FILTERS_POPUP_Z_INDEX}}}
            >
              {options.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )
      }

      case 'datePicker':
        return (
          <DatePicker
            label={item.label}
            value={values[item.id] ? dayjs(values[item.id]) : null}
            onChange={(val) => setField(item.id, val)}
            slotProps={{
              textField: {size: 'small', fullWidth: true},
              popper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
              dialog: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
              mobilePaper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
            }}
            format="DD MMM YYYY"
          />
        )

      case 'timePicker':
        return (
          <TimePicker
            label={item.label}
            value={values[item.id] ? dayjs(values[item.id]) : null}
            onChange={(val) => setField(item.id, val)}
            slotProps={{
              textField: {size: 'small', fullWidth: true},
              popper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
              dialog: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
              mobilePaper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
            }}
            format="HH:mm"
          />
        )

      case 'rangePicker': {
        const range = rangeDates[item.id] ?? [null, null]
        return (
          <Box sx={{display: 'flex', gap: 1}}>
            <DatePicker
              value={range[0]}
              onChange={(val) => setRangeDates(prev => ({...prev, [item.id]: [val, range[1]]}))}
              slotProps={{
                textField: {size: 'small', fullWidth: true, placeholder: `${item.label} da`},
                popper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
                dialog: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
                mobilePaper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
              }}
              format="DD MMM YYYY"
            />
            <DatePicker
              value={range[1]}
              onChange={(val) => setRangeDates(prev => ({...prev, [item.id]: [range[0], val]}))}
              slotProps={{
                textField: {size: 'small', fullWidth: true, placeholder: `${item.label} a`},
                popper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
                dialog: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
                mobilePaper: {sx: {zIndex: FILTERS_POPUP_Z_INDEX}},
              }}
              format="DD MMM YYYY"
            />
          </Box>
        )
      }

      case 'range': {
        const sliderVal = values[item.id] ?? [item.min ?? 0, item.max ?? 100]
        return (
          // panelBody scrolls with overflowY: 'auto', which per the CSS overflow spec
          // forces overflowX to compute to 'auto' too (an axis can't stay 'visible' once
          // the other scrolls) — without this horizontal breathing room the thumb, which
          // sits a few px outside the track at 0%/100%, gets clipped by that new scrollport.
          // The default thumb is 20px (10px radius), so 1 spacing unit (8px) isn't quite
          // enough — 1.5 (12px) clears it with a small margin.
          <Box sx={{px: 1.5}}>
            <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            <Slider
              value={sliderVal}
              onChange={(_, val) => setField(item.id, val)}
              min={item.min ?? 0}
              max={item.max ?? 100}
              step={item.step ?? 1}
              valueLabelDisplay="auto"
              valueLabelFormat={item.valueLabelFormat as any}
              sx={{mt: 1}}
            />
          </Box>
        )
      }

      case 'checkbox':
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{fontSize: 12, mb: 0.5}}>{item.label}</FormLabel>
            <FormGroup row>
              {getOptions(item).map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      size="small"
                      checked={(values[item.id] ?? []).includes(opt.value)}
                      onChange={(e) => {
                        const current: string[] = values[item.id] ?? []
                        setField(
                          item.id,
                          e.target.checked
                            ? [...current, opt.value]
                            : current.filter((v: string) => v !== opt.value),
                        )
                      }}
                    />
                  }
                  label={<Typography variant="body2">{opt.label}</Typography>}
                />
              ))}
            </FormGroup>
          </FormControl>
        )

      case 'patientLookup':
      case 'doctorLookup': {
        const lookupType = item.type as LookupType
        return (
          <TextField
            fullWidth
            size="small"
            label={item.label}
            value={values[item.id] ?? ''}
            inputProps={{readOnly: true}}
            sx={{'& input': {cursor: 'default', color: 'text.secondary'}}}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setLookupOpen({id: item.id, type: lookupType})}
                  >
                    <SearchIcon fontSize="small" />
                  </IconButton>
                  {values[item.id] && (
                    <IconButton size="small" onClick={() => clearLookup(item.id)}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />
        )
      }

      default:
        return (
          <TextField
            fullWidth
            size="small"
            label={item.label}
            value={values[item.id] ?? ''}
            onChange={e => setField(item.id, e.target.value)}
          />
        )
    }
  }

  const isOpen = Boolean(anchorEl)

  const panelHeader = (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3}}>
      <Typography variant="h3">Filtri</Typography>
      <IconButton size="small" onClick={handleClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )

  const panelBody = (
    // minHeight: 0 overrides the flex item's default min-height:auto: without it,
    // this area refuses to shrink below its fields' full height, so on a small
    // viewport it pushes panelFooter (Azzera/Cerca) past the Drawer Paper's
    // maxHeight — leaving the action buttons off-screen with no way to scroll to
    // them (same class of bug as AppDialog's DialogContent, see comment there).
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3, flex: 1, overflowY: 'auto', minHeight: 0, pt: 0.8}}>
      {config.map(item => (
        <Box key={item.id}>
          {renderField(item)}
        </Box>
      ))}
    </Box>
  )

  const panelFooter = (
    <Box sx={{display: 'flex', gap: 1, mt: 3, flexShrink: 0}}>
      <Button variant="secondary" fullWidth onClick={handleReset}>
        Azzera
      </Button>
      <Button variant="primary" fullWidth onClick={handleApply}>
        Cerca
      </Button>
    </Box>
  )

  const drawerPaperSx: SxProps<Theme> = isMobile
    ? {
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        // `dvh`, not `vh` — see the identical maxHeight comment on AppDialog's
        // PaperProps.sx: on a real phone `vh` assumes the address/tab bar is
        // hidden, so `90vh` can size this fixed-to-bottom sheet taller than
        // what's actually visible, pushing panelFooter below the reachable area.
        maxHeight: '90dvh',
        // Matches AppDialog's Paper: keeps the sheet itself from growing past
        // maxHeight and bleeding over the page below once panelBody's own
        // minHeight: 0 lets it be the one that scrolls internally instead.
        overflow: 'hidden',
        pt: 1.5,
        px: 3,
        pb: 3,
        display: 'flex',
        flexDirection: 'column',
      }
    : {
        width: 480,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
      }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <Button
        variant="primary"
        startIcon={<FilterListIcon />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        {triggerLabel}
      </Button>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={isOpen}
        onClose={handleClose}
        PaperProps={{sx: drawerPaperSx}}
        // MUI's default Drawer zIndex (theme.zIndex.drawer = 1200) is lower than
        // Dialog/Modal's (theme.zIndex.modal = 1300). Filtra è usato anche dentro
        // dialog (es. ActivityDetailDialogNew), quindi senza questo override il
        // pannello si apre visivamente sotto il dialog che lo ospita.
        sx={{zIndex: FILTERS_DRAWER_Z_INDEX}}
      >
        {isMobile && (
          <Box sx={{width: 40, height: 4, borderRadius: 2, bgcolor: NEUTRAL_SCALE[300], mx: 'auto', mb: 2}} />
        )}
        {panelHeader}
        {panelBody}
        {panelFooter}
      </Drawer>

      {lookupOpen?.type === 'patientLookup' && (
        <PatientLookupDialog
          onClose={() => setLookupOpen(null)}
          onRowClick={(row) => {
            setField(lookupOpen.id, `${row.firstName} ${row.lastName}`)
            setLookupData(prev => ({...prev, [lookupOpen.id]: row}))
            setLookupOpen(null)
          }}
        />
      )}
      {lookupOpen?.type === 'doctorLookup' && (
        <DoctorsLookupDialog
          customFilters={doctorCustomFilters}
          onClose={() => setLookupOpen(null)}
          onRowClick={(row) => {
            setField(lookupOpen.id, `${row.firstName} ${row.lastName}`)
            setLookupData(prev => ({...prev, [lookupOpen.id]: row._id}))
            setLookupOpen(null)
          }}
        />
      )}
    </LocalizationProvider>
  )
}
